import type { FC } from 'react';
import { useTranslation } from 'react-i18next';

import { useAddRiskToEnterpriseRisk } from '@/hooks/mutations/enterprise-risk';

import { ModalForm } from '../form/form/ModalForm';
import InstantiateEnterpriseRiskForm from './AddToEnterpriseRiskForm';
import type { SchemaFields } from './schema';
import { schema } from './schema';

interface Props {
  riskId: string;
  entityId: null | string | undefined;
  enterpriseRiskId: null | string | undefined;
  riskTier: number;
  isVisible: boolean;
  onDismiss: () => void;
  modalSubType: 'enterpriseRisk' | 'entity';
}

const AddToEnterpriseRiskModal: FC<Props> = ({
  isVisible,
  onDismiss,
  riskId,
  riskTier,
  enterpriseRiskId,
  entityId,
  modalSubType,
}) => {
  const { t } = useTranslation(['common']);
  const { addRiskToEnterpriseRisk } = useAddRiskToEnterpriseRisk();

  const onSubmit = async (data: SchemaFields) => {
    await addRiskToEnterpriseRisk([
      {
        // In entity mode the enterprise-risk select is never rendered, so the
        // field keeps its `''` default. Send null — an entity-only link has no
        // enterprise risk, and `''` is not a uuid.
        EnterpriseRiskId: data.EnterpriseRiskId || null,
        EntityId: data.EntityId,
        RiskId: riskId,
      },
    ]);
  };

  // Prefill the current link so the user can see what they are replacing —
  // re-linking to a different enterprise risk or entity replaces the
  // existing link.
  const initialValues = {
    EntityId: entityId ?? '',
    EnterpriseRiskId: enterpriseRiskId ?? '',
  };

  return (
    <ModalForm<SchemaFields>
      onDismiss={onDismiss}
      visible={isVisible}
      values={initialValues}
      onSave={onSubmit}
      formId={'instantiateEnterpriseRisk'}
      schema={schema({
        currentEnterpriseRiskId: enterpriseRiskId,
        currentEntityId: entityId,
        modalSubType,
        errorMessage: t(
          'enterpriseRisks.addRiskToEnterpriseRisk.duplicate_error'
        ),
      })}
      i18n={t('enterpriseRisks')}
      defaultValues={initialValues}
    >
      <InstantiateEnterpriseRiskForm
        riskTier={riskTier}
        entityId={entityId}
        enterpriseRiskId={enterpriseRiskId}
        modalSubType={modalSubType}
      />
    </ModalForm>
  );
};

export default AddToEnterpriseRiskModal;
